/**
 * The ledger's routes (piece 23). 🔨 Relative to the API's own root, never
 * prefixed with /api: the base URL carries the prefix.
 */
import { DEFAULT_CURRENCY_CODE, dollarsToCents, formatCurrency } from '@bms2/shared';
export const ACCOUNTS_PATH = '/accounting/accounts';
/** Decision 10120: Set Opening Balance on one account, the account against 3100 on the opening route. */
export const accountOpeningBalancePath = (id: string): string => `${ACCOUNTS_PATH}/${encodeURIComponent(id)}/opening-balance`;
export const JOURNAL_PATH = '/accounting/journal';
/** Decision 0761: one journal entry as its document page reads it, and the Reverse its footer offers. */
export const journalEntryPath = (id: string): string => `${JOURNAL_PATH}/${encodeURIComponent(id)}`;
export const journalReversePath = (id: string): string => `${JOURNAL_PATH}/${encodeURIComponent(id)}/reverse`;
/** Decision 11520: Save as Draft on a new journal, and Post on a Draft's page (its Save is a PUT to the entry's own path). */
export const JOURNAL_DRAFTS_PATH = `${JOURNAL_PATH}/drafts`;
export const journalPostDraftPath = (id: string): string => `${JOURNAL_PATH}/${encodeURIComponent(id)}/post`;
/** Decision 11944: Void on a Draft's page. */
export const journalVoidPath = (id: string): string => `${JOURNAL_PATH}/${encodeURIComponent(id)}/void`;
/** Decision 1075: every entry one document is the source of, which the Account Journal tile reads. */
export const documentJournalPath = (sourceDocumentType: string, sourceDocumentId: string): string => `${JOURNAL_PATH}/document/${encodeURIComponent(sourceDocumentType)}/${encodeURIComponent(sourceDocumentId)}`;
export const PERIODS_PATH = '/accounting/periods';
export const PERIOD_REOPENS_PATH = '/accounting/periods/reopens';
export const BLOCKED_POSTINGS_PATH = '/accounting/blocked-postings';
/** Piece 24, decision 11020: the recurring templates, Make Recurring (a POST here) and End. */
export const RECURRING_PATH = '/accounting/recurring';
export const recurringEndPath = (id: string): string => `${RECURRING_PATH}/${encodeURIComponent(id)}/end`;
// Piece 23, decision 0407: the ledger's self-checks — the drift queue and the tie-outs run on demand.
export const DRIFT_PATH = '/accounting/drift';
export const TIE_OUTS_PATH = '/accounting/tie-outs';
// Piece 27, decision 0223: the customer statement — computed live, sent one at a time or in a batch.
export const STATEMENTS_LIVE_PATH = '/statements/live';
export const STATEMENTS_SENT_PATH = '/statements/sent';
export const STATEMENTS_SEND_PATH = '/statements/send';
export const STATEMENTS_BATCH_PATH = '/statements/batch';
/** The sent statement's own sheet, rendered on its print template through the one seam (joined 2026-09-18). */
export const statementPdfPath = (id: string): string => `/statements/${encodeURIComponent(id)}/pdf`;
// Piece 25, decision 0425: the tax register's own read, and the routes its acts post to.
export const TAX_PATH = '/tax';
export const TAX_REGISTER_PATH = '/tax/register';
// Decision 1524 (T787): what one state taxes, read when that state opens rather than every state's answers with the page.
export const TAX_RULES_PATH = '/tax/rules';
export const taxRulesPath = (stateCode: string): string => `${TAX_RULES_PATH}?stateCode=${encodeURIComponent(stateCode)}`;
// Decision 0677: the forms library's list, and one committed form's bytes, inline or as an attachment.
export const TAX_FORMS_PATH = '/tax/forms';
export const taxFormPath = (file: string, download = false): string => `${TAX_FORMS_PATH}/${encodeURIComponent(file)}${download ? '?download=1' : ''}`;

/** The screens' own routes, the ones the seeded NavItem and WorkQueue rows name. */
export const CHART_OF_ACCOUNTS_ROUTE = '/accounting/chart-of-accounts';
export const MANUAL_JOURNAL_ROUTE = '/accounting/journal';
export const PERIODS_ROUTE = '/accounting/periods';
export const RECURRING_ROUTE = '/accounting/recurring';
export const BLOCKED_POSTINGS_ROUTE = '/accounting/blocked-postings';
export const LEDGER_DRIFT_ROUTE = '/accounting/drift';
/** Decision 0677: the forms library, which the certificate window links. */
export const TAX_FORMS_ROUTE = '/tax-forms';
export const STATEMENTS_ROUTE = '/accounting/statements';

/** Integer cents as the person reads them; a credit balance the ledger stores negative is presented by the caller first. */
export function money(cents: number, currencyCode: string = DEFAULT_CURRENCY_CODE): string {
  return formatCurrency({ amount: cents, currencyCode });
}

/** A typed dollar amount as integer cents, or null for a blank; the page never sends a fraction of a cent. */
export function centsOf(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(/,/g, ''));
  if (!Number.isFinite(value)) return null;
  return dollarsToCents(value);
}
